import {Pause, Play} from '@nlabs/gothamjs/icons';
import {useEffect, useRef, useState} from 'react';

import type {ReactElement} from 'react';

interface AmbientVideoProps {
  readonly poster: string;
  readonly src: string;
  readonly title: string;
}

export const AmbientVideo = ({poster, src, title}: AmbientVideoProps): ReactElement => {
  const ref = useRef<HTMLVideoElement>(null);
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(true);
  const [userPlaying, setUserPlaying] = useState<boolean>();
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const shouldPlay = inView && visible && (userPlaying ?? !reducedMotion);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = (): void => setReducedMotion(media.matches);
    const updateVisibility = (): void => setVisible(document.visibilityState === 'visible');
    updateMotion();
    updateVisibility();
    media.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', updateVisibility);
    const observer = new IntersectionObserver(([entry]) => {
      setInView(Boolean(entry?.isIntersecting && entry.intersectionRatio > 0));
    }, {threshold: [0, 0.2]});
    if(ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
      media.removeEventListener('change', updateMotion);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);
  useEffect(() => {
    if(shouldPlay) {
      setLoaded(true);
    }
    const video = ref.current;
    if(!video) {
      return;
    }
    if(shouldPlay && loaded && !failed) {
      void video.play().catch(() => setPlaying(false));
    } else {
      video.pause();
    }
  }, [failed, loaded, shouldPlay, userPlaying]);

  const togglePlayback = (): void => {
    setUserPlaying(!playing);
    if(playing) {
      ref.current?.pause();
    }
  };

  return <>
    <video aria-label={title} className="h-full w-full object-cover" disablePictureInPicture loop muted onError={() => setFailed(true)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} playsInline poster={poster} preload="none" ref={ref} src={loaded ? src : undefined} />
    {failed && <img alt={title} className="absolute inset-0 h-full w-full object-cover" src={poster} />}
    {!failed && <button aria-label={`${playing ? 'Pause' : 'Play'} ${title}`} className="absolute top-4 right-4 z-10 flex size-10 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white backdrop-blur-sm transition hover:bg-black/70 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet-300" onClick={togglePlayback} type="button">{playing ? <Pause aria-hidden="true" size={16} /> : <Play aria-hidden="true" size={16} />}</button>}
  </>;
};
